import React, { useState } from 'react';
import {
    List, Datagrid, TextField, BooleanField, EditButton, DeleteButton,
    Edit, Create, SimpleForm, TextInput, SelectInput, BooleanInput,
    ArrayInput, SimpleFormIterator,
    useNotify, usePermissions, useRecordContext, required,
} from 'react-admin';
import { useController } from 'react-hook-form';
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useGithubConfig, GitHubImage } from './ra_contentEditor';
import { deleteFile, getFile, uploadBinary } from './githubApi';

const TYPE_CHOICES = [
    { id: 'team', name: 'Team' },
    { id: 'client', name: 'Client' },
];

const SOCIAL_TYPE_CHOICES = ['facebook', 'instagram', 'linkedin', 'email', 'web', 'phone', 'whatsapp']
    .map(t => ({ id: t, name: t }));

// Strip undefined so Firestore never rejects the payload
const sanitizePerson = (values) => ({
    type: values.type ?? 'team',
    title: values.title ?? '',
    description: values.description ?? '',
    image: values.image ?? '',
    visibility: values.visibility ?? true,
    contactable: values.contactable ?? false,
    socials: values.socials ?? [],
});

// ---------------------------------------------------------------------------
// Image widget: preview + upload + delete file from GitHub (same pattern as
// ra_services.js's ServiceImageField)
// ---------------------------------------------------------------------------

function PersonImageField() {
    const record = useRecordContext();
    const notify = useNotify();
    const { ghConfig } = useGithubConfig();
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [buster, setBuster] = useState(0);
    const ref = React.useRef();

    const { field: imageField } = useController({ name: 'image', defaultValue: record?.image ?? '' });
    const currentImage = imageField.value;

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !ghConfig) return;
        setUploading(true);
        try {
            const buf = await file.arrayBuffer();
            let sha = null;
            try { ({ sha } = await getFile(ghConfig.pat, ghConfig.owner, ghConfig.repo, `people/${file.name}`, ghConfig.branch)); } catch {}
            await uploadBinary(ghConfig.pat, ghConfig.owner, ghConfig.repo, `people/${file.name}`, ghConfig.branch, buf, sha, `[admin] upload person photo ${file.name}`);
            imageField.onChange(file.name);
            setBuster(b => b + 1);
            notify(`Uploaded ${file.name}.`, { type: 'success' });
        } catch (err) {
            notify(`Upload failed: ${err.message}`, { type: 'error' });
        }
        setUploading(false);
        e.target.value = '';
    };

    const handleDelete = async () => {
        if (!currentImage || !ghConfig) return;
        setDeleting(true);
        try {
            const { sha } = await getFile(ghConfig.pat, ghConfig.owner, ghConfig.repo, `people/${currentImage}`, ghConfig.branch);
            await deleteFile(ghConfig.pat, ghConfig.owner, ghConfig.repo, `people/${currentImage}`, ghConfig.branch, sha, `[admin] delete person photo ${currentImage}`);
            imageField.onChange('');
            setBuster(b => b + 1);
            notify(`Deleted ${currentImage}.`, { type: 'success' });
        } catch (err) {
            notify(`Delete failed: ${err.message}`, { type: 'error' });
        }
        setDeleting(false);
    };

    return (
        <Box mb={1}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Photo</Typography>
            {currentImage && ghConfig && (
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Box sx={{ width: 72, height: 72, border: '1px solid rgba(128,128,128,0.4)', borderRadius: 1, overflow: 'hidden', flexShrink: 0 }}>
                        <GitHubImage key={buster} pat={ghConfig.pat} owner={ghConfig.owner} repo={ghConfig.repo}
                            branch={ghConfig.branch} filePath={`people/${currentImage}`}
                            imgStyle={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </Box>
                    <Box>
                        <Typography variant="body2">{currentImage}</Typography>
                        <Tooltip title="Delete file from GitHub and clear field">
                            <span>
                                <IconButton size="small" color="error" onClick={handleDelete} disabled={deleting}>
                                    {deleting ? <CircularProgress size={16} /> : <DeleteForeverIcon fontSize="small" />}
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                </Box>
            )}
            <Box display="flex" gap={1} alignItems="center">
                <input type="file" accept="image/*" ref={ref} style={{ display: 'none' }} onChange={handleUpload} />
                <Button variant="outlined" size="small"
                    startIcon={uploading ? <CircularProgress size={14} /> : <UploadFileIcon />}
                    onClick={() => ref.current.click()} disabled={uploading}>
                    {uploading ? 'Uploading…' : currentImage ? 'Replace photo' : 'Upload photo'}
                </Button>
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// CRUD components
// ---------------------------------------------------------------------------

export const PeopleList = (props) => {
    const { permissions } = usePermissions();
    return (
        <List {...props} title="People">
            <Datagrid rowClick="edit">
                <TextField source="title" label="Name" />
                <TextField source="type" label="Type" />
                <BooleanField source="visibility" label="Visible" />
                {permissions >= 98 && <EditButton />}
                {permissions >= 99 && <DeleteButton />}
            </Datagrid>
        </List>
    );
};

const PersonForm = () => (
    <>
        <TextInput source="title" label="Name" validate={required()} fullWidth />
        <SelectInput source="type" label="Type" choices={TYPE_CHOICES} defaultValue="team" validate={required()} />
        <TextInput source="description" label="Role / description" multiline rows={2} fullWidth defaultValue="" />
        <PersonImageField />
        <TextInput source="image" label="Photo filename" fullWidth />
        <Box display="flex" gap={3} my={1}>
            <BooleanInput source="visibility" label="Visible on site" defaultValue={true} />
            <BooleanInput source="contactable" label="Show on Contact page" defaultValue={false} />
        </Box>
        <ArrayInput source="socials" label="Socials">
            <SimpleFormIterator>
                <SelectInput source="type" label="Type" choices={SOCIAL_TYPE_CHOICES} defaultValue="web" />
                <TextInput source="link" label="URL / value" fullWidth />
            </SimpleFormIterator>
        </ArrayInput>
    </>
);

export const PeopleEdit = (props) => (
    <Edit {...props} title="Edit Person" transform={sanitizePerson}>
        <SimpleForm>
            <PersonForm />
        </SimpleForm>
    </Edit>
);

export const PeopleCreate = (props) => (
    <Create {...props} title="New Person" transform={sanitizePerson}>
        <SimpleForm>
            <PersonForm />
        </SimpleForm>
    </Create>
);
